import { useState, type ReactNode } from 'react';
import { ChevronDown, Map as MapIcon, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { FeatureItemProps } from '../LayersTab/FeatureItem';
import { FeatureSymbol } from '../LayersTab/FeatureSymbol';
import { InternalObjectSymbol } from '../LayersTab/InternalObjectSymbol';

/**
 * SitStat-local shadcn FeatureItem — prop-compatible with LayersTab
 * FeatureItem.
 */
export function SitStatFeatureItem({
  internalFeature,
  externalFeature,
  title,
  onZoom,
  children,
  onEdit,
  onDelete,
  isEditing = false,
}: FeatureItemProps) {
  const [open, setOpen] = useState(false);
  const featureId = (internalFeature?.featureId ??
    externalFeature?.featureId) as string;

  return (
    <li className="tw:!m-0 tw:!w-full tw:list-none tw:p-0">
      <Card
        size="sm"
        className={cn(
          'dark tw:w-full tw:gap-0 tw:!border tw:!border-solid tw:!border-foreground/35 tw:bg-card tw:py-0 tw:text-left tw:text-foreground tw:shadow-sm',
          isEditing && 'tw:ring-2 tw:ring-primary'
        )}
      >
        <Collapsible open={open} onOpenChange={setOpen}>
          <div className="tw:flex tw:items-center tw:gap-1 tw:px-2 tw:py-1.5">
            <CollapsibleTrigger
              render={
                <button
                  type="button"
                  className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-2 tw:rounded-md tw:px-1 tw:py-0.5 tw:text-left tw:hover:bg-accent/60"
                >
                  <ChevronDown
                    className={cn(
                      'tw:size-3.5 tw:shrink-0 tw:text-muted-foreground tw:transition-transform',
                      open && 'tw:rotate-180'
                    )}
                  />
                  {internalFeature ? (
                    <InternalObjectSymbol
                      resourceType={internalFeature.resourceType}
                      properties={internalFeature.properties}
                    />
                  ) : (
                    <FeatureSymbol symbol={externalFeature?.symbol} />
                  )}
                  <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-left tw:text-xs tw:font-medium">
                    {title}
                  </span>
                </button>
              }
            />

            <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-0.5">
              {onEdit ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Edit object"
                  title="Edit object"
                  onClick={(event) => {
                    event.stopPropagation();
                    void onEdit(featureId);
                  }}
                >
                  <Pencil className="tw:h-3.5 tw:w-3.5" />
                </Button>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label="Zoom to map location"
                title="Zoom to map location"
                onClick={(event) => {
                  event.stopPropagation();
                  void onZoom();
                }}
              >
                <MapIcon className="tw:h-3.5 tw:w-3.5" />
              </Button>
              {onDelete ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Delete object"
                  title="Delete object"
                  onClick={(event) => {
                    event.stopPropagation();
                    void onDelete(featureId);
                  }}
                >
                  <Trash2 className="tw:h-3.5 tw:w-3.5" />
                </Button>
              ) : null}
            </div>
          </div>

          <CollapsibleContent className="tw:space-y-1.5 tw:border-t tw:px-2.5 tw:py-2">
            {children as ReactNode}
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </li>
  );
}
